/**
 * ChecklistPopup - Floating popup showing onboarding checklist
 *
 * Displays a compact checklist of features free users can explore,
 * with progress tracking and recommended next steps.
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { supabase } from '@/lib/supabase/client';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  X,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  MessageSquare,
  Calculator,
  Upload,
  Target,
  UserSearch,
  FileText,
  Crown,
} from 'lucide-react';

interface ChecklistItem {
  id: string;
  label: string;
  description: string;
  icon: React.ElementType;
  path: string;
  freeLimit: string;
  checkComplete: () => Promise<boolean> | boolean;
  priority: number;
}

interface ChecklistPopupProps {
  open: boolean;
  onClose: () => void;
  onItemClick?: () => void;
}

export function ChecklistPopup({ open, onClose, onItemClick }: ChecklistPopupProps) {
  const { t } = useTranslation('app');
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const { getUsageStats, isPaid } = useSubscription();

  const [completedItems, setCompletedItems] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);

  // Checklist items in optimal order for building AI foundation
  const checklistItems: ChecklistItem[] = [
    {
      id: 'financial',
      label: t('gettingStarted.financial.label', 'Financial Profile'),
      description: t('gettingStarted.financial.description', 'Complete your financial snapshot'),
      icon: FileText,
      path: '/net-worth',
      freeLimit: 'Included',
      checkComplete: async () => {
        if (!user) return false;
        const [{ data: assets }, { data: debts }, { data: income }] = await Promise.all([
          supabase.from('assets').select('id').eq('user_id', user.id).limit(1),
          supabase.from('debts').select('id').eq('user_id', user.id).limit(1),
          supabase.from('income_sources').select('id').eq('user_id', user.id).limit(1),
        ]);
        return (assets?.length ?? 0) > 0 || (debts?.length ?? 0) > 0 || (income?.length ?? 0) > 0;
      },
      priority: 1,
    },
    {
      id: 'goals',
      label: t('gettingStarted.goals.label', 'Set Your Goals'),
      description: t('gettingStarted.goals.description', 'Define what matters most to you'),
      icon: Target,
      path: '/goals',
      freeLimit: 'Unlimited',
      checkComplete: () => profile?.goals_complete ?? false,
      priority: 2,
    },
    {
      id: 'spouse',
      label: t('gettingStarted.spouse.label', 'Know Your Spouse'),
      description: t('gettingStarted.spouse.description', 'Build a personality profile'),
      icon: UserSearch,
      path: '/know-your-spouse',
      freeLimit: 'Step 1 free',
      checkComplete: async () => {
        if (!user) return false;
        const { data } = await supabase
          .from('opponent_profiles')
          .select('id')
          .eq('user_id', user.id)
          .limit(1);
        return (data?.length ?? 0) > 0;
      },
      priority: 3,
    },
    {
      id: 'documents',
      label: t('gettingStarted.documents.label', 'Upload Documents'),
      description: t('gettingStarted.documents.description', 'Add statements for AI analysis'),
      icon: Upload,
      path: '/documents',
      freeLimit: '3 uploads',
      checkComplete: () => {
        const stats = getUsageStats();
        return stats.documents.used > 0;
      },
      priority: 4,
    },
    {
      id: 'support',
      label: t('gettingStarted.support.label', 'Calculate Support'),
      description: t('gettingStarted.support.description', 'Estimate child support & alimony'),
      icon: Calculator,
      path: '/support-calculator',
      freeLimit: '1 calculation',
      checkComplete: () => {
        const stats = getUsageStats();
        return stats.supportCalculations.used > 0;
      },
      priority: 5,
    },
    {
      id: 'strategy',
      label: t('gettingStarted.strategy.label', 'Generate Strategy'),
      description: t('gettingStarted.strategy.description', 'See your personalized settlement strategy'),
      icon: Sparkles,
      path: '/strategy',
      freeLimit: 'Preview',
      checkComplete: async () => {
        if (!user) return false;
        const { data } = await supabase
          .from('strategies')
          .select('id')
          .eq('user_id', user.id)
          .limit(1);
        return (data?.length ?? 0) > 0;
      },
      priority: 6,
    },
    {
      id: 'sage',
      label: t('gettingStarted.sage.label', 'Ask Sage AI'),
      description: t('gettingStarted.sage.description', 'Get personalized answers'),
      icon: MessageSquare,
      path: '/ask-sage',
      freeLimit: '10 messages',
      checkComplete: async () => {
        if (!user) return false;
        const { data } = await supabase
          .from('sage_conversations')
          .select('id')
          .eq('user_id', user.id)
          .limit(1);
        return (data?.length ?? 0) > 0;
      },
      priority: 7,
    },
  ];

  // Check completion status
  useEffect(() => {
    if (!open) return;

    const checkAllItems = async () => {
      setIsLoading(true);
      const completed = new Set<string>();

      for (const item of checklistItems) {
        try {
          const isComplete = await item.checkComplete();
          if (isComplete) {
            completed.add(item.id);
          }
        } catch (e) {
          console.error(`Error checking ${item.id}:`, e);
        }
      }

      setCompletedItems(completed);
      setIsLoading(false);
    };

    checkAllItems();
  }, [open, user, profile]);

  const completedCount = completedItems.size;
  const totalCount = checklistItems.length;
  const progress = (completedCount / totalCount) * 100;

  // Find next recommended action
  const nextAction = checklistItems.find((item) => !completedItems.has(item.id));

  const handleItemClick = (path: string) => {
    navigate(path);
    onItemClick?.();
    onClose();
  };

  if (!open) return null;

  return (
    <div className="absolute bottom-full right-0 mb-3 w-80 sm:w-96 animate-in slide-in-from-bottom-2 fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-slate-900">Get Started with SettleWise</h3>
            <button
              onClick={onClose}
              className="p-1 rounded-full hover:bg-amber-100 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Progress bar */}
          <div className="flex items-center gap-3">
            <Progress value={progress} className="h-2 flex-1" />
            <span className="text-xs font-medium text-amber-700 whitespace-nowrap">
              {completedCount} of {totalCount}
            </span>
          </div>
        </div>

        {/* Checklist items */}
        <div className="max-h-80 overflow-y-auto">
          <div className="p-2">
            {checklistItems.map((item) => {
              const isComplete = completedItems.has(item.id);
              const Icon = item.icon;

              return (
                <button
                  key={item.id}
                  onClick={() => handleItemClick(item.path)}
                  className={cn(
                    'w-full flex items-center gap-3 p-2.5 rounded-lg transition-all text-left',
                    isComplete
                      ? 'bg-green-50 hover:bg-green-100'
                      : 'hover:bg-slate-50'
                  )}
                >
                  {/* Status icon */}
                  <div
                    className={cn(
                      'p-1.5 rounded-full flex-shrink-0',
                      isComplete ? 'bg-green-100' : 'bg-slate-100'
                    )}
                  >
                    {isComplete ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : (
                      <Icon className="h-4 w-4 text-slate-500" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p
                      className={cn(
                        'font-medium text-sm truncate',
                        isComplete ? 'text-green-800' : 'text-slate-900'
                      )}
                    >
                      {item.label}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{item.description}</p>
                  </div>

                  {/* Badge */}
                  <Badge
                    variant="outline"
                    className={cn(
                      'text-[10px] whitespace-nowrap flex-shrink-0',
                      isComplete
                        ? 'border-green-300 text-green-700 bg-green-50'
                        : 'border-amber-300 text-amber-700 bg-amber-50'
                    )}
                  >
                    {isComplete ? 'Done' : item.freeLimit}
                  </Badge>

                  {/* Arrow */}
                  {!isComplete && (
                    <ArrowRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer - Next action or upgrade CTA */}
        <div className="p-3 border-t border-slate-100 bg-slate-50">
          {completedCount === totalCount ? (
            <div className="flex items-center gap-3 p-2 bg-gradient-to-r from-amber-100 to-orange-100 rounded-lg">
              <Crown className="h-5 w-5 text-amber-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-amber-900">All features explored!</p>
                <p className="text-xs text-amber-700">Upgrade for full access</p>
              </div>
              <Button
                size="sm"
                onClick={() => {
                  navigate('/pricing');
                  onClose();
                }}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                Upgrade
              </Button>
            </div>
          ) : nextAction ? (
            <Button
              onClick={() => handleItemClick(nextAction.path)}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
            >
              <nextAction.icon className="h-4 w-4 mr-2" />
              {nextAction.label}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default ChecklistPopup;
